import { useDocumentReferencesForBinaryIds } from '@ctw/shared/api/fhir/binaries';
import { useDocumentReferences } from '@ctw/shared/api/fhir/document';
import type { DocumentModel } from '@ctw/shared/api/fhir/models/document';
import type { EncounterAdtMessage } from '@ctw/shared/api/fhir/models/encounter';
import { Button } from '@ctw/shared/components/button';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { adtMessageTitle } from '@ctw/shared/content/related-documents/adt-message-labels';
import {
  RelatedDocumentNarratives,
  useNarrativeForBinary,
} from '@ctw/shared/content/related-documents/related-document-narratives';
import {
  joinWithMiddleDot,
  SourceDocumentHeader,
} from '@ctw/shared/content/related-documents/source-document-header';
import { ViewSourceDocumentButton } from '@ctw/shared/content/related-documents/view-source-document-button';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import { faChevronRight, faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';

export interface RelatedDocumentsListProps {
  binaryIds: Array<string>;
  // ADT messages identified only by DocumentReference id, not Binary id. Requires `upid` to
  // resolve.
  adtMessages?: Array<EncounterAdtMessage>;
  // The encounter's own location, shown on every ADT row's network/provider line.
  adtSourceLocationDisplay?: string;
  upid?: string;
  heading?: string;
}

export const RelatedDocumentsList = ({
  binaryIds,
  adtMessages = [],
  adtSourceLocationDisplay,
  upid,
  heading = 'Source Documents',
}: RelatedDocumentsListProps) => {
  const adtDocumentReferenceIds = adtMessages.map((message) => message.documentReferenceId);

  const {
    data: resolvedAdtDocuments = [],
    isLoading: isLoadingAdtDocuments,
    isError: isAdtDocumentsError,
  } = useDocumentReferences(upid ?? '', adtDocumentReferenceIds);

  const { data: resolvedBinaryDocuments = [] } = useDocumentReferencesForBinaryIds({
    upid: upid ?? '',
    binaryIds,
    enabled: binaryIds.length > 0 && upid !== undefined && upid !== '',
    includeCustodianOrganization: true,
  });

  if (binaryIds.length === 0 && adtMessages.length === 0) {
    return null;
  }

  const documentByBinaryId = new Map(
    resolvedBinaryDocuments
      .filter((document): document is DocumentModel & { binaryId: string } =>
        Boolean(document.binaryId),
      )
      .map((document) => [document.binaryId, document]),
  );
  const adtMessageByDocumentReferenceId = new Map(
    adtMessages.map((message) => [message.documentReferenceId, message]),
  );

  // Binary ids already shown as an ADT message, once that message has resolved.
  const binaryIdsShownAsAdt = new Set(
    resolvedAdtDocuments
      .map((document) => document.binaryId)
      .filter((binaryId): binaryId is string => binaryId !== undefined),
  );

  // Related binaries MUST render above ADT documents, regardless of the order the batched ADT
  // fetch resolves in. Concatenating in this order, and rendering from the single resulting
  // array, makes that grouping an explicit property of this list rather than an accident of
  // where each JSX block happens to sit. Neither group has an existing sort convention of its
  // own, so each keeps its incoming order.
  const orderedEntries: Array<RelatedDocumentListEntry> = [
    ...binaryIds
      .filter((binaryId) => !binaryIdsShownAsAdt.has(binaryId))
      .map(
        (binaryId): RelatedDocumentListEntry => ({
          kind: 'binary',
          binaryId,
          document: documentByBinaryId.get(binaryId),
        }),
      ),
    ...resolvedAdtDocuments.map(
      (document): RelatedDocumentListEntry => ({
        kind: 'adt',
        document,
        adtMessage: adtMessageByDocumentReferenceId.get(document.id),
      }),
    ),
  ];

  return (
    <div className={tw`space-y-2`}>
      <div className={tw`font-semibold text-lg`}>{heading}</div>
      <ul className={tw`m-0 list-none space-y-1 p-0`}>
        {orderedEntries.map((entry) =>
          entry.kind === 'binary' ? (
            <RelatedDocumentItem
              key={`binary-${entry.binaryId}`}
              binaryId={entry.binaryId}
              document={entry.document}
            />
          ) : (
            <AdtDocumentItem
              key={`adt-${entry.document.id}`}
              document={entry.document}
              messageType={entry.adtMessage?.messageType}
              effectiveTimeDisplay={entry.adtMessage?.effectiveTimeDisplay}
              sourceLocationDisplay={adtSourceLocationDisplay}
            />
          ),
        )}
        {adtMessages.length > 0 && isLoadingAdtDocuments && (
          <div className={tw`rounded-md border border-border-main px-3 py-4 shadow-sm`}>
            <LoadingSpinner message="Loading document..." centered={true} />
          </div>
        )}
        {adtMessages.length > 0 && isAdtDocumentsError && (
          <div
            className={tw`flex items-center gap-2 rounded-md border border-border-main px-3 py-4 text-content-subtle shadow-sm`}
          >
            <FontAwesomeIcon icon={faCircleExclamation} className={tw`h-5 w-5`} />
            <div className={tw`font-medium text-[1rem] tracking-wide`}>
              Unable to load these documents
            </div>
          </div>
        )}
      </ul>
    </div>
  );
};

type RelatedDocumentListEntry =
  | { kind: 'binary'; binaryId: string; document?: DocumentModel }
  | { kind: 'adt'; document: DocumentModel; adtMessage?: EncounterAdtMessage };

interface AdtDocumentItemProps {
  document: DocumentModel;
  messageType?: string;
  effectiveTimeDisplay?: string;
  sourceLocationDisplay?: string;
}

// A document identified only by DocumentReference id (e.g. an Encounter's ADT message). Already
// resolved by the parent list, so this renders without its own narrative expand/collapse, and its
// title is plain text rather than a link — there's no narrative to open.
const AdtDocumentItem = ({
  document,
  messageType,
  effectiveTimeDisplay,
  sourceLocationDisplay,
}: AdtDocumentItemProps) => (
  <div
    className={tw`flex w-full min-w-full items-center rounded-md border border-border-main shadow-sm`}
  >
    <div className={tw`flex w-full flex-1 items-center gap-3 px-3 py-4`}>
      <SourceDocumentHeader
        title={adtMessageTitle(messageType)}
        clinicalDateDisplay={effectiveTimeDisplay}
        badgeLabel="ADT"
        sourceLine={joinWithMiddleDot('ADT feed', document.networkDisplay, sourceLocationDisplay)}
        receivedDisplay={document.createdAtDisplay}
      />
    </div>
    <ViewSourceDocumentButton
      resourceType="DocumentReference"
      resourceId={document.id}
      contentType={document.contentType}
      enabled={true}
    />
  </div>
);

export interface RelatedDocumentItemProps {
  binaryId: string;
  document?: DocumentModel;
}

export const RelatedDocumentItem = ({ binaryId, document }: RelatedDocumentItemProps) => {
  const { isLoading, isError, data } = useNarrativeForBinary({ binaryId });
  const canDisplayBinaryLink = !(isLoading || isError) && data !== undefined;
  const hasNarrative = (data?.cda?.content?.sections?.length ?? 0) > 0;
  const displayTitle = data?.title ?? data?.cda?.metadata?.document?.title ?? 'Untitled Document';

  const isCollapsible = canDisplayBinaryLink && hasNarrative;
  const [isExpanded, setIsExpanded] = useState(true);
  const showsNarrative = isCollapsible && isExpanded;
  const narrativeRegionId = `related-document-narrative-${binaryId}`;

  return (
    <div className={tw`rounded-md border border-border-main shadow-sm`}>
      <div
        className={twx('flex w-full min-w-full items-center', {
          'rounded-b-none border-border-main border-b': showsNarrative,
        })}
      >
        <Button
          type="button"
          variant="link"
          className={tw`flex flex-1 items-center gap-1 hover:opacity-80`}
          ariaLabel={isExpanded ? 'Collapse document' : 'Expand document'}
          ariaExpanded={isExpanded}
          ariaControls={isCollapsible ? narrativeRegionId : undefined}
          disabled={!isCollapsible}
          onClick={() => {
            setIsExpanded((previous) => !previous);
          }}
        >
          <div className={tw`flex w-full items-center gap-3 px-3 py-4`}>
            {isCollapsible && (
              <FontAwesomeIcon
                icon={faChevronRight}
                className={twx('h-4 w-4 shrink-0 text-content-icon transition-transform', {
                  'rotate-90': isExpanded,
                })}
              />
            )}
            <div className={tw`block text-left`}>
              {isLoading && <LoadingSpinner message="Loading document..." centered={true} />}
              {isError && (
                <div className={tw`flex items-center gap-2 text-content-subtle`}>
                  <FontAwesomeIcon icon={faCircleExclamation} className={tw`h-5 w-5`} />
                  <div className={tw`font-medium text-[1rem] tracking-wide`}>
                    Unable to load this document
                  </div>
                </div>
              )}
              {canDisplayBinaryLink && (
                <SourceDocumentHeader
                  title={displayTitle}
                  clinicalDateDisplay={document?.dateCreated}
                  badgeLabel={document?.badgeLabel}
                  sourceLine={joinWithMiddleDot(
                    document?.networkDisplay,
                    document?.providerDisplay,
                  )}
                  receivedDisplay={document?.createdAtDisplay}
                />
              )}
            </div>
          </div>
        </Button>
        <ViewSourceDocumentButton
          resourceType="Binary"
          resourceId={binaryId}
          contentType={data?.contentType}
          enabled={canDisplayBinaryLink}
        />
      </div>
      {showsNarrative && <RelatedDocumentNarratives binaryId={binaryId} />}
    </div>
  );
};
